<template>
  <div
    :dir="currentDir"
    class="modal fade ld-modal"
    id="retrieve-device-modal"
    tabindex="-1"
  >
    <div class="modal-dialog modal-dialog-centered ld-dialog">
      <div class="modal-content ld-content">
        <!-- Close Button -->
        <button type="button" class="ld-close-btn" data-bs-dismiss="modal">
          <i class="fas fa-times"></i>
        </button>

        <!-- Icon Header -->
        <div class="ld-icon-header">
          <div class="ld-icon-circle">
            <i class="fas fa-mobile-alt"></i>
          </div>
          <div class="ld-step-indicator">
            <span :class="{ active: step === 1 }">1</span>
            <span class="ld-step-line" :class="{ active: step === 2 }"></span>
            <span :class="{ active: step === 2 }">2</span>
          </div>
        </div>

        <div class="ld-body">
          <!-- Title -->
          <h4 class="ld-title">
            {{
              step === 1 ? $t("auth.ld_title_step1") : $t("auth.ld_title_step2")
            }}
          </h4>
          <p class="ld-subtitle">
            {{
              step === 1 ? $t("auth.ld_intro_step1") : $t("auth.ld_intro_step2")
            }}
          </p>

          <!-- Success Message -->
          <div v-if="successMessage" class="ld-success-msg">
            <i class="fas fa-check-circle"></i>
            {{ successMessage }}
          </div>

          <!-- Error Message -->
          <div v-if="errorMessage" class="ld-error-msg">
            <i class="fas fa-exclamation-circle"></i>
            {{ errorMessage }}
          </div>

          <!-- Telegram Verification Section -->
          <div
            v-if="
              step === 1 &&
              selectedMethod === 'telegram' &&
              showTelegramVerification
            "
            class="ld-telegram-section"
          >
            <div class="ld-telegram-icon">
              <i class="fab fa-telegram"></i>
            </div>
            <h6>{{ $t("auth.telegram_verification") }}</h6>
            <p>{{ telegramMessage }}</p>
            <div class="ld-telegram-btns">
              <a
                v-if="botLink"
                :href="botLink"
                target="_blank"
                class="ld-btn ld-btn-telegram"
              >
                <i class="fab fa-telegram"></i> {{ $t("auth.start_chat") }}
              </a>
              <button
                class="ld-btn ld-btn-primary"
                @click="checkTelegramVerification"
                :disabled="isLoading"
              >
                <i class="fas fa-check-circle"></i>
                {{
                  isLoading
                    ? $t("common.verifying")
                    : $t("auth.completed_verification")
                }}
              </button>
            </div>
          </div>

          <!-- Step 1: Phone Input -->
          <div v-if="step === 1" class="ld-form">
            <div class="ld-input-group">
              <label
                ><i class="fas fa-phone"></i>
                {{ $t("auth.ld_phone_label") }}</label
              >
              <input id="devicePhone" type="tel" class="ld-input" dir="ltr" />
            </div>

            <!-- Send Method Selection -->
            <div class="ld-methods">
              <label class="ld-methods-label"
                ><i class="fas fa-paper-plane"></i>
                {{ $t("auth.ld_send_method_label") }}</label
              >
              <div class="ld-methods-grid">
                <label
                  class="ld-method-card"
                  :class="{ selected: selectedMethod === 'sms' }"
                >
                  <input type="radio" value="sms" v-model="selectedMethod" />
                  <div class="ld-method-icon sms">
                    <i class="fas fa-sms"></i>
                  </div>
                  <span>{{ $t("auth.ld_method_sms") }}</span>
                </label>
                <label
                  class="ld-method-card"
                  :class="{ selected: selectedMethod === 'whatsapp' }"
                >
                  <input
                    type="radio"
                    value="whatsapp"
                    v-model="selectedMethod"
                  />
                  <div class="ld-method-icon whatsapp">
                    <i class="fab fa-whatsapp"></i>
                  </div>
                  <span>{{ $t("auth.ld_method_whatsapp") }}</span>
                </label>
                <label
                  class="ld-method-card"
                  :class="{ selected: selectedMethod === 'email' }"
                >
                  <input type="radio" value="email" v-model="selectedMethod" />
                  <div class="ld-method-icon email">
                    <i class="fas fa-envelope"></i>
                  </div>
                  <span>{{ $t("auth.ld_method_email") }}</span>
                </label>
                <label
                  class="ld-method-card"
                  :class="{ selected: selectedMethod === 'telegram' }"
                >
                  <input
                    type="radio"
                    value="telegram"
                    v-model="selectedMethod"
                  />
                  <div class="ld-method-icon telegram">
                    <i class="fab fa-telegram"></i>
                  </div>
                  <span>{{ $t("auth.fp_method_telegram") }}</span>
                </label>
              </div>
            </div>

            <button
              class="ld-btn ld-btn-primary ld-btn-full"
              :disabled="isLoading"
              @click="sendPhone"
              v-if="
                !(selectedMethod === 'telegram' && showTelegramVerification)
              "
            >
              <template v-if="isLoading">
                <span class="ld-spinner"></span> {{ $t("auth.ld_sending") }}
              </template>
              <template v-else-if="selectedMethod === 'telegram'">
                <i class="fab fa-telegram"></i>
                {{ $t("auth.verify_with_telegram") }}
              </template>
              <template v-else>
                <i class="fas fa-paper-plane"></i> {{ $t("auth.ld_send_code") }}
              </template>
            </button>
          </div>

          <!-- Step 2: OTP Verification -->
          <div v-else class="ld-form">
            <div class="ld-input-group">
              <label
                ><i class="fas fa-key"></i> {{ $t("auth.ld_otp_label") }}</label
              >
              <input
                v-model="otp"
                type="text"
                class="ld-input"
                :placeholder="$t('auth.ld_otp_placeholder')"
              />
            </div>

            <div class="ld-btn-group">
              <button
                class="ld-btn ld-btn-secondary"
                :disabled="isLoading"
                @click="goBack"
              >
                <i class="fas fa-arrow-right"></i> {{ $t("common.back") }}
              </button>
              <button
                class="ld-btn ld-btn-primary"
                :disabled="isLoading"
                @click="verifyOtp"
              >
                <template v-if="isLoading">
                  <span class="ld-spinner"></span>
                </template>
                <template v-else>
                  <i class="fas fa-check"></i>
                  {{ $t("auth.ld_restore_device") }}
                </template>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import { apiUrl } from "@/utils/api";
import i18n from "@/plugins/i18n";

var itiDevice;

export default {
  computed: {
    currentDir() {
      return this.$i18n && this.$i18n.locale === "ar" ? "rtl" : "ltr";
    },
  },
  data() {
    return {
      step: 1,
      otp: "",
      isLoading: false,
      errorMessage: "",
      successMessage: "",
      selectedMethod: "sms", // default
      showTelegramVerification: false,
      telegramMessage: "",
      botLink: "",
    };
  },
  mounted() {
    const modal = document.getElementById("retrieve-device-modal");
    const phone = document.getElementById("devicePhone");

    modal.addEventListener("shown.bs.modal", () => {
      itiDevice = intlTelInput(phone, {
        utilsScript: "assets/js/utils.js",
        initialCountry: "ly",
        separateDialCode: true,
        excludeCountries: ["il"],
        customPlaceholder: function (
          selectedCountryPlaceholder,
          selectedCountryData
        ) {
          // Remove leading 0 from placeholder (e.g., 0922345678 -> 922345678)
          return selectedCountryPlaceholder.replace(/^0/, "");
        },
      });
    });
  },
  methods: {
    goBack() {
      this.errorMessage = "";
      this.successMessage = "";
      this.otp = "";
      this.step = 1;
      this.selectedMethod = "sms";
      this.$nextTick(() => {
        const phone = document.getElementById("devicePhone");
        itiDevice = intlTelInput(phone, {
          utilsScript: "assets/js/utils.js",
          initialCountry: "ly",
          separateDialCode: true,
          excludeCountries: ["il"],
          customPlaceholder: function (
            selectedCountryPlaceholder,
            selectedCountryData
          ) {
            // Remove leading 0 from placeholder (e.g., 0922345678 -> 922345678)
            return selectedCountryPlaceholder.replace(/^0/, "");
          },
        });
      });
    },

    async sendPhone() {
      this.errorMessage = "";
      this.successMessage = "";

      if (this.selectedMethod === "telegram") {
        try {
          this.isLoading = true;
          const phoneNumber = itiDevice.getNumber();
          const response = await this.sendTelegramRequest(phoneNumber);

          this.telegramMessage = response.data.message;
          this.botLink = response.data.bot_link;

          if (response.data.bot_link === null) {
            // User already has Telegram linked, proceed to OTP
            this.successMessage = i18n.global.t("auth.telegram_linked_success");
            this.step = 2;
          } else {
            // Show Telegram verification section
            this.showTelegramVerification = true;
          }
        } catch (error) {
          this.errorMessage =
            error.message || i18n.global.t("auth.ld_send_error");
        } finally {
          this.isLoading = false;
        }
        return;
      }

      // Original flow for other methods (SMS, WhatsApp, Email)
      try {
        this.isLoading = true;
        const phoneNumber = itiDevice.getNumber();

        const payload = {
          phone: phoneNumber,
          country_code: "+" + itiDevice.getSelectedCountryData().dialCode,
          sms: this.selectedMethod === "sms" ? 1 : 0,
          whatsapp: this.selectedMethod === "whatsapp" ? 1 : 0,
          email: this.selectedMethod === "email" ? 1 : 0,
        };

        const res = await fetch(`${apiUrl}/lost-master-device`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(
            errData.message || i18n.global.t("auth.ld_send_error")
          );
        }

        this.successMessage = i18n.global.t("auth.ld_sent_success");
        this.step = 2;
      } catch (err) {
        this.errorMessage = err.message || i18n.global.t("auth.ld_send_error");
      } finally {
        this.isLoading = false;
      }
    },

    async verifyOtp() {
      this.errorMessage = "";
      if (!this.otp) {
        this.errorMessage = i18n.global.t("auth.ld_required_otp");
        return;
      }
      this.isLoading = true;
      try {
        const res = await fetch(`${apiUrl}/retrieve-master-device`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ otp: this.otp }),
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(
            errData.message || i18n.global.t("auth.ld_verify_error")
          );
        }

        Swal.fire(
          i18n.global.t("auth.ld_restore_success_title"),
          i18n.global.t("auth.ld_restore_success_text"),
          "success"
        );
        this.goBack();
      } catch (err) {
        this.errorMessage =
          err.message || i18n.global.t("auth.ld_verify_error");
      } finally {
        this.isLoading = false;
      }
    },

    async sendTelegramRequest(phoneNumber) {
      const response = await fetch(`${apiUrl}/lost-master-device`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: phoneNumber,
          country_code: "+" + itiDevice.getSelectedCountryData().dialCode,
          telegram: 1,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || i18n.global.t("auth.ld_send_error")
        );
      }

      return await response.json();
    },

    async checkTelegramVerification() {
      try {
        this.isLoading = true;
        const phoneNumber = itiDevice.getNumber();
        const response = await this.sendTelegramRequest(phoneNumber);

        if (response.data.bot_link === null) {
          // User has linked Telegram, proceed to OTP
          this.showTelegramVerification = false;
          this.successMessage = i18n.global.t("auth.telegram_linked_success");
          this.step = 2;
        } else {
          // Show success message with the bot link
          await Swal.fire({
            title: i18n.global.t("auth.telegram_verification"),
            text: response.data.message,
            icon: "info",
            confirmButtonText: i18n.global.t("common.ok"),
            confirmButtonColor: "#198754",
            showCancelButton: true,
            cancelButtonText: i18n.global.t("common.cancel"),
            cancelButtonColor: "#6c757d",
          });

          // Update the verification message and bot link
          this.telegramMessage = response.data.message;
          this.botLink = response.data.bot_link;
        }
      } catch (error) {
        this.errorMessage =
          error.message || i18n.global.t("auth.ld_send_error");
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ========== Animations ========== */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes iconBounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

/* ========== Modal Dialog ========== */
.ld-modal .ld-dialog {
  max-width: 420px;
}

.ld-content {
  border-radius: 24px !important;
  border: none !important;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15);
  overflow: visible;
  background: #fff;
  animation: fadeIn 0.3s ease;
  position: relative;
  padding: 0 !important;
}

/* ========== Close Button ========== */
.ld-close-btn {
  position: absolute;
  top: -12px;
  right: -12px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #fff;
  border: none;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10;
}

.ld-close-btn i {
  color: #6b7280;
  font-size: 14px;
}

.ld-close-btn:hover {
  background: #ef4444;
  transform: rotate(90deg);
}

.ld-close-btn:hover i {
  color: #fff;
}

/* ========== Icon Header ========== */
.ld-icon-header {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%);
  padding: 30px 20px 25px;
  text-align: center;
  border-radius: 24px 24px 0 0;
  position: relative;
}

.ld-icon-circle {
  width: 70px;
  height: 70px;
  background: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  animation: iconBounce 2s ease-in-out infinite;
}

.ld-icon-circle i {
  font-size: 28px;
  color: #00c444;
}

/* Step Indicator */
.ld-step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.ld-step-indicator span:not(.ld-step-line) {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.ld-step-indicator span.active {
  background: #fff;
  color: #00c444;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.ld-step-line {
  width: 40px !important;
  height: 3px !important;
  background: rgba(255, 255, 255, 0.3) !important;
  border-radius: 2px !important;
}

.ld-step-line.active {
  background: #fff !important;
}

/* ========== Body ========== */
.ld-body {
  padding: 25px;
}

.ld-title {
  text-align: center;
  color: #1f2937;
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 8px;
}

.ld-subtitle {
  text-align: center;
  color: #6b7280;
  font-size: 0.9rem;
  margin: 0 0 20px;
  line-height: 1.5;
}

/* ========== Messages ========== */
.ld-success-msg,
.ld-error-msg {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 16px;
  animation: slideUp 0.3s ease;
}

.ld-success-msg {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  color: #047857;
  border: 1px solid #a7f3d0;
}

.ld-error-msg {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  color: #dc2626;
  border: 1px solid #fecaca;
}

/* ========== Form ========== */
.ld-form {
  animation: slideUp 0.3s ease;
}

.ld-input-group {
  margin-bottom: 16px;
}

.ld-input-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.ld-input-group label i {
  color: #00c444;
  font-size: 0.9rem;
}

.ld-input {
  width: 100%;
  height: 48px;
  padding: 0 16px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 15px;
  transition: all 0.3s ease;
  background: #f9fafb;
}

.ld-input:focus {
  outline: none;
  border-color: #00c444;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(0, 196, 68, 0.1);
}

/* ========== Methods Selection ========== */
.ld-methods {
  margin-bottom: 20px;
}

.ld-methods-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
}

.ld-methods-label i {
  color: #00c444;
}

.ld-methods-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.ld-method-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 14px 10px;
  border: 2px solid #e5e7eb;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f9fafb;
}

.ld-method-card input {
  display: none;
}

.ld-method-card:hover {
  border-color: #a7f3d0;
  background: #f0fdf4;
}

.ld-method-card.selected {
  border-color: #00c444;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  box-shadow: 0 4px 15px rgba(0, 196, 68, 0.15);
}

.ld-method-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  transition: all 0.3s ease;
}

.ld-method-icon.sms {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #d97706;
}

.ld-method-icon.whatsapp {
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  color: #16a34a;
}

.ld-method-icon.email {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #2563eb;
}

.ld-method-icon.telegram {
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
  color: #0ea5e9;
}

.ld-method-card.selected .ld-method-icon {
  transform: scale(1.1);
}

.ld-method-card span {
  font-size: 0.75rem;
  font-weight: 600;
  color: #4b5563;
}

.ld-method-card.selected span {
  color: #047857;
}

/* ========== Buttons ========== */
.ld-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 24px;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
}

.ld-btn-primary {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%);
  color: #fff;
  box-shadow: 0 4px 15px rgba(0, 196, 68, 0.3);
}

.ld-btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 196, 68, 0.4);
}

.ld-btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.ld-btn-secondary {
  background: #f3f4f6;
  color: #374151;
  border: 2px solid #e5e7eb;
}

.ld-btn-secondary:hover {
  background: #e5e7eb;
}

.ld-btn-telegram {
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: #fff;
  box-shadow: 0 4px 15px rgba(14, 165, 233, 0.3);
}

.ld-btn-telegram:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(14, 165, 233, 0.4);
}

.ld-btn-full {
  width: 100%;
}

.ld-btn-group {
  display: flex;
  gap: 12px;
}

.ld-btn-group .ld-btn {
  flex: 1;
}

/* Spinner */
.ld-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* ========== Telegram Section ========== */
.ld-telegram-section {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 2px solid #bae6fd;
  border-radius: 16px;
  padding: 20px;
  text-align: center;
  margin-bottom: 16px;
}

.ld-telegram-icon {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
}

.ld-telegram-icon i {
  font-size: 1.5rem;
  color: #fff;
}

.ld-telegram-section h6 {
  color: #0369a1;
  font-weight: 700;
  margin: 0 0 8px;
}

.ld-telegram-section p {
  color: #64748b;
  font-size: 0.85rem;
  margin: 0 0 16px;
}

.ld-telegram-btns {
  display: flex;
  gap: 10px;
}

.ld-telegram-btns .ld-btn {
  flex: 1;
  padding: 12px 16px;
  font-size: 0.85rem;
}

/* ========== intl-tel-input ========== */
:deep(.iti) {
  width: 100%;
}

:deep(.iti__selected-flag) {
  border-radius: 10px 0 0 10px;
  padding: 0 10px;
  background: #f3f4f6;
}

/* ========== DARK MODE ========== */
[data-bs-theme="dark"] .ld-content,
[data-theme="dark"] .ld-content,
.dark .ld-content {
  background: #1f2937;
}

[data-bs-theme="dark"] .ld-close-btn,
[data-theme="dark"] .ld-close-btn,
.dark .ld-close-btn {
  background: #374151;
}

[data-bs-theme="dark"] .ld-close-btn i,
[data-theme="dark"] .ld-close-btn i,
.dark .ld-close-btn i {
  color: #9ca3af;
}

[data-bs-theme="dark"] .ld-icon-circle,
[data-theme="dark"] .ld-icon-circle,
.dark .ld-icon-circle {
  background: #374151;
}

[data-bs-theme="dark"] .ld-icon-circle i,
[data-theme="dark"] .ld-icon-circle i,
.dark .ld-icon-circle i {
  color: #00e050;
}

[data-bs-theme="dark"] .ld-title,
[data-theme="dark"] .ld-title,
.dark .ld-title {
  color: #f3f4f6;
}

[data-bs-theme="dark"] .ld-subtitle,
[data-theme="dark"] .ld-subtitle,
.dark .ld-subtitle {
  color: #9ca3af;
}

[data-bs-theme="dark"] .ld-input-group label,
[data-theme="dark"] .ld-input-group label,
.dark .ld-input-group label {
  color: #e5e7eb;
}

[data-bs-theme="dark"] .ld-input,
[data-theme="dark"] .ld-input,
.dark .ld-input {
  background: #374151;
  border-color: #4b5563;
  color: #f3f4f6;
}

[data-bs-theme="dark"] .ld-input:focus,
[data-theme="dark"] .ld-input:focus,
.dark .ld-input:focus {
  background: #374151;
  border-color: #00c444;
}

[data-bs-theme="dark"] .ld-methods-label,
[data-theme="dark"] .ld-methods-label,
.dark .ld-methods-label {
  color: #e5e7eb;
}

[data-bs-theme="dark"] .ld-method-card,
[data-theme="dark"] .ld-method-card,
.dark .ld-method-card {
  background: #374151;
  border-color: #4b5563;
}

[data-bs-theme="dark"] .ld-method-card:hover,
[data-theme="dark"] .ld-method-card:hover,
.dark .ld-method-card:hover {
  border-color: #00c444;
  background: #3f4a5a;
}

[data-bs-theme="dark"] .ld-method-card.selected,
[data-theme="dark"] .ld-method-card.selected,
.dark .ld-method-card.selected {
  background: linear-gradient(135deg, #1a3d2a 0%, #1e4d35 100%);
  border-color: #00c444;
}

[data-bs-theme="dark"] .ld-method-card span,
[data-theme="dark"] .ld-method-card span,
.dark .ld-method-card span {
  color: #d1d5db;
}

[data-bs-theme="dark"] .ld-btn-secondary,
[data-theme="dark"] .ld-btn-secondary,
.dark .ld-btn-secondary {
  background: #374151;
  border-color: #4b5563;
  color: #e5e7eb;
}

[data-bs-theme="dark"] .ld-telegram-section,
[data-theme="dark"] .ld-telegram-section,
.dark .ld-telegram-section {
  background: linear-gradient(135deg, #1e3a5f 0%, #1e3a4f 100%);
  border-color: #2563eb;
}

[data-bs-theme="dark"] .ld-telegram-section h6,
[data-theme="dark"] .ld-telegram-section h6,
.dark .ld-telegram-section h6 {
  color: #7dd3fc;
}

[data-bs-theme="dark"] .ld-telegram-section p,
[data-theme="dark"] .ld-telegram-section p,
.dark .ld-telegram-section p {
  color: #94a3b8;
}

[data-bs-theme="dark"] :deep(.iti__selected-flag),
[data-theme="dark"] :deep(.iti__selected-flag),
.dark :deep(.iti__selected-flag) {
  background: #374151;
}

/* ========== Responsive ========== */
@media (max-width: 480px) {
  .ld-modal .ld-dialog {
    max-width: 100%;
    margin: 0.5rem;
  }

  .ld-methods-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .ld-method-card {
    padding: 12px 6px;
  }

  .ld-method-icon {
    width: 36px;
    height: 36px;
    font-size: 1rem;
  }

  .ld-method-card span {
    font-size: 0.7rem;
  }

  .ld-btn-group {
    flex-direction: column;
  }

  .ld-telegram-btns {
    flex-direction: column;
  }
}
</style>
